import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  styled,
  Theme,
  Switch,
  Fab,
  Snackbar,
  Alert,
  IconButton
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ImageSelectionPopup from './ImageSelectionPopup';
import { t } from '../i18n';

// API base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003';

// Maximum number of active listings allowed per account
const MAX_ACTIVE_LISTINGS = 50;

// Accounts that show a countdown to each listing's next re-upload
const REFRESH_COUNTDOWN_USERS = ['autojokr'];

// Accounts that can set a refresh interval per car, on top of the account-wide
// one. An empty selection means the car follows the account setting.
const PER_LISTING_INTERVAL_USERS = ['autopaar'];

// Accounts that can flag individual cars for "topovanie" on Bazos
const TOPOVAT_USERS = ['lerent', 'autojokr'];
const PER_LISTING_INTERVAL_OPTIONS = [2, 3, 4, 5, 7, 8, 14];

// Time left until a listing is deleted and re-posted, as "2d 5h".
// Returns null when the account has no interval, the car was never uploaded,
// or automatic refreshing is switched off.
function formatTimeUntilRefresh(uploadTimestamp?: string, interval?: number | 'off' | null): string | null {
  if (!uploadTimestamp || typeof interval !== 'number' || interval <= 0) return null;
  const uploadedAt = Date.parse(uploadTimestamp);
  if (isNaN(uploadedAt)) return null;

  const msLeft = uploadedAt + interval * 24 * 60 * 60 * 1000 - Date.now();
  if (msLeft <= 0) return '0d 0h';

  const days = Math.floor(msLeft / (24 * 60 * 60 * 1000));
  const hours = Math.floor((msLeft % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  return `${days}d ${hours}h`;
}

// Update the fallback image paths to point to the SVG
const PLACEHOLDER_IMAGE = '/assets/car-placeholder.svg';

// Styled Switch component to match design
const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 1,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#000000',
        opacity: 1,
        border: 0,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#000000',
      border: '6px solid #fff',
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22,
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 24 / 2,
    backgroundColor: '#000000',
    opacity: 1,
    transition: 'background-color 500ms',
  },
}));

// Styled components
const ListingsContainer = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: '16px',
  margin: theme.spacing(2, 0),

  // Mobile optimization
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
    gap: '12px',
    margin: theme.spacing(1, 0),
  },

  // Tablet optimization
  '@media (min-width: 601px) and (max-width: 960px)': {
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '14px',
  },

  // Desktop optimization - ensure 4+ columns on wider screens
  '@media (min-width: 961px)': {
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '16px',
  }
}));

const ListingCard = styled(Paper)(({ theme }: { theme: Theme }) => ({
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  borderRadius: '8px',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: theme.shadows[6],
  },
  
  // Mobile-specific styles
  '@media (max-width: 600px)': {
    '&:hover': {
      transform: 'translateY(-2px)', // Less dramatic hover effect on mobile
    }
  }
}));

const ListingImage = styled('img')({
  width: '100%',
  height: '160px',
  objectFit: 'cover',
  display: 'block',
  pointerEvents: 'none',
  
  // Mobile optimization
  '@media (max-width: 600px)': {
    height: '140px',
  }
});

const ListingDetails = styled(Box)(({ theme }: { theme: Theme }) => ({
  padding: theme.spacing(2),
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  
  // Mobile optimization
  '@media (max-width: 600px)': {
    padding: theme.spacing(1.5),
  }
}));

const ListingTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontWeight: 600,
  marginBottom: theme.spacing(1),
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  height: '50px',
  pointerEvents: 'none',
  fontSize: '1rem',
  
  // Mobile optimization
  '@media (max-width: 600px)': {
    fontSize: '0.9rem',
    height: '44px',
    marginBottom: theme.spacing(0.5),
  }
}));

const ListingFooter = styled(Box)(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: 'auto',
  paddingTop: theme.spacing(1),
  
  // Mobile optimization
  '@media (max-width: 600px)': {
    paddingTop: theme.spacing(0.5),
  }
}));

interface Listing {
  id?: string;
  title: string;
  photo?: string;
  price?: string;
  link?: string;
  active: boolean;
  imageIds?: string[];
  uploadTimestamp?: string;
  refreshInterval?: number;
  topovat?: boolean;
}

interface UserData {
  username: string;
  xmlLink: string;
}

interface AutobazarListingsProps {
  onListingsChange?: (count: number) => void;
  onActiveListingsChange?: (activeCount: number) => void;
}

// Compact pill select matching the interval buttons on the page. It carries its
// own state: plain while the car follows the account, filled once it has an
// interval of its own, so an override is visible at a glance across the grid.
const CHEVRON_DARK = "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23333333'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E\")";
const CHEVRON_LIGHT = "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffffff'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E\")";

const IntervalSelect = styled('select')({
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  fontSize: '12px',
  fontWeight: 500,
  fontFamily: 'inherit',
  lineHeight: 1.5,
  padding: '4px 24px 4px 10px',
  borderRadius: '14px',
  border: '1px solid #ccc',
  backgroundColor: '#fff',
  color: '#333',
  cursor: 'pointer',
  backgroundImage: CHEVRON_DARK,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 6px center',
  backgroundSize: '12px',
  transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',

  '&:hover': {
    borderColor: '#888',
    backgroundColor: '#f5f5f5',
  },
  '&:focus-visible': {
    outline: 'none',
    borderColor: '#1976d2',
    boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.25)',
  },

  // A car with its own interval reads as "selected", like the active interval button
  '&[data-override="true"]': {
    backgroundColor: '#333',
    borderColor: '#333',
    color: '#fff',
    backgroundImage: CHEVRON_LIGHT,
  },
  '&[data-override="true"]:hover': {
    backgroundColor: '#555',
    borderColor: '#555',
  },

  '@media (max-width: 600px)': {
    fontSize: '11px',
    padding: '3px 22px 3px 8px',
  },

  // The dropdown list itself always renders on white
  '& option': {
    backgroundColor: '#fff',
    color: '#333',
  },
});

// Styled floating save button
const FloatingSaveButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  backgroundColor: '#1976d2',
  color: 'white',
  minWidth: '120px',
  height: '56px',
  borderRadius: '28px',
  fontSize: '16px',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#1565c0',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
  },
  zIndex: 1000,
  
  // Mobile optimization
  '@media (max-width: 600px)': {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    minWidth: '100px',
    height: '48px',
    fontSize: '14px',
    borderRadius: '24px',
  }
}));

export default function AutobazarListings({ onListingsChange, onActiveListingsChange }: AutobazarListingsProps) {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [changes, setChanges] = useState<Record<string, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showLimitAlert, setShowLimitAlert] = useState(false);
  const [updateInterval, setUpdateInterval] = useState<number | 'off' | null>(null);
  const [currentUsername, setCurrentUsername] = useState<string>('');

  const showRefreshCountdown = REFRESH_COUNTDOWN_USERS.includes(currentUsername);
  const showPerListingInterval = PER_LISTING_INTERVAL_USERS.includes(currentUsername);
  const showTopovat = TOPOVAT_USERS.includes(currentUsername);

  // Flag or unflag one car for topovanie
  const handleToggleTopovat = async (listingId: string, next: boolean) => {
    setListings(prevListings =>
      prevListings.map(listing =>
        listing.id === listingId ? { ...listing, topovat: next } : listing
      )
    );

    try {
      const response = await axios.post(`${API_URL}/api/update-listing-topovat`, {
        username: currentUsername,
        listingId,
        topovat: next
      });
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || 'Failed to update topovat');
      }
    } catch (error) {
      console.error('Error updating topovat:', error);
      // Put the icon back where it was so it never shows a state the DB does not have
      setListings(prevListings =>
        prevListings.map(listing =>
          listing.id === listingId ? { ...listing, topovat: !next } : listing
        )
      );
      setShowErrorMessage(true);
    }
  };

  // Set (or clear) one car's own refresh interval
  const handleListingIntervalChange = async (listingId: string, value: string) => {
    const interval = value === '' ? null : Number(value);

    // Reflect the choice straight away, then persist it
    setListings(prevListings =>
      prevListings.map(listing =>
        listing.id === listingId
          ? { ...listing, refreshInterval: interval === null ? undefined : interval }
          : listing
      )
    );

    try {
      const response = await axios.post(`${API_URL}/api/update-listing-interval`, {
        username: currentUsername,
        listingId,
        interval
      });
      if (!response.data || !response.data.success) {
        throw new Error(response.data?.message || 'Failed to update listing interval');
      }
    } catch (error) {
      console.error('Error updating listing interval:', error);
      setShowErrorMessage(true);
    }
  };

  // Keep the countdown honest if the page is left open for hours
  const [, setClockTick] = useState(0);
  useEffect(() => {
    if (!showRefreshCountdown) return;
    const timer = setInterval(() => setClockTick(tick => tick + 1), 5 * 60 * 1000);
    return () => clearInterval(timer);
  }, [showRefreshCountdown]);

  // Update parent with listings count whenever listings change
  useEffect(() => {
    if (onListingsChange) {
      onListingsChange(listings.length);
    }
    
    // Calculate and update active listings count
    if (onActiveListingsChange) {
      const activeCount = listings.filter(listing => listing.active).length;
      onActiveListingsChange(activeCount);
    }
  }, [listings, onListingsChange, onActiveListingsChange]);

  // Fetch listings from the server that handles XML parsing
  useEffect(() => {
    async function fetchListings() {
      try {
        setLoading(true);
        
        // Get the username from localStorage
        let username = '';
        const storedUser = localStorage.getItem('user');
        
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            username = parsedUser.username || '';
          } catch (err) {
            console.error('Error parsing stored user:', err);
          }
        }
        
        // Fallback to direct username if necessary
        if (!username) {
          username = localStorage.getItem('username') || '';
        }
        
        // Fallback to a test username if we still don't have one
        if (!username) {
          username = 'mtautos'; // Use a default test username
        }

        // Fetch user data to get XML link
        const userResponse = await axios.get(`${API_URL}/api/user-data`, {
          params: { username }
        });

        if (userResponse.data && userResponse.data.user) {
          setUserData({
            username: username,
            xmlLink: userResponse.data.user.xml_link
          });
        }
        
        // Call our new server endpoint that handles XML parsing
        const response = await axios.get(`${API_URL}/api/autobazar-listings`, {
          params: { username }
        });
        
        if (response.data && response.data.listings) {
          setListings(response.data.listings);
          setCurrentUsername(username);
          if (response.data.updateInterval !== undefined) {
            setUpdateInterval(response.data.updateInterval);
          }
        } else {
          setError('No listings data received from server');
        }
      } catch (err) {
        console.error('Error fetching listings:', err);
        setError('Failed to load listings');
      } finally {
        setLoading(false);
      }
    }

    fetchListings();
  }, []);

  // Handle listing click to open image selection popup
  const handleListingClick = (listing: Listing) => {
    if (!userData) {
      console.error('User data not available');
      return;
    }
    
    setSelectedListing(listing);
    setPopupOpen(true);
  };

  // Close popup
  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedListing(null);
  };

  // Handle successful save to refresh listings
  const handleSaveSuccess = async () => {
    try {
      // Get the username from localStorage
      let username = '';
      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          username = parsedUser.username || '';
        } catch (err) {
          console.error('Error parsing stored user:', err);
          username = 'autocentrumbb'; // Default fallback
        }
      } else {
        username = 'autocentrumbb'; // Default fallback
      }
      
      // Refresh listings to get updated imageIds
      const response = await axios.get(`${API_URL}/api/autobazar-listings`, {
        params: { username }
      });
      
      if (response.data && response.data.listings) {
        setListings(response.data.listings);
      }
    } catch (error) {
      console.error('Error refreshing listings after save:', error);
    }
  };

  // Toggle a listing's active state (local only)
  const handleToggleActive = (id: string, event: React.ChangeEvent<HTMLInputElement>) => {
    // Prevent the listing click event from firing
    event.stopPropagation();

    // Check if user is trying to activate a listing
    const currentListing = listings.find(listing => listing.id === id);
    if (currentListing && !currentListing.active) {
      // Count current active listings
      const activeCount = listings.filter(listing => listing.active).length;

      // Check if limit would be exceeded
      if (activeCount >= MAX_ACTIVE_LISTINGS) {
        setShowLimitAlert(true);
        return; // Don't allow activation
      }
    }

    // Update local state immediately for UI responsiveness
    setListings(prevListings =>
      prevListings.map(listing =>
        listing.id === id ? { ...listing, active: !listing.active } : listing
      )
    );

    // Track this change
    if (currentListing) {
      const newActiveState = !currentListing.active;
      setChanges(prevChanges => ({
        ...prevChanges,
        [id]: newActiveState
      }));
      setHasChanges(true);
    }
  };

  // Save all changes to the server
  const handleSaveChanges = async () => {
    if (!hasChanges || saving) return;
    
    setSaving(true);
    setShowErrorMessage(false);
    
    try {
      // Get the username
      let username = '';
      const storedUser = localStorage.getItem('user');
      
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          username = parsedUser.username || '';
        } catch (err) {
          console.error('Error parsing stored user:', err);
        }
      }
      
      if (!username) {
        username = localStorage.getItem('username') || '';
      }
      
      if (!username) {
        username = 'autocentrumbb'; // Default fallback
      }
      
      // Send all changes to the server
      const updatePromises = Object.entries(changes).map(([listingId, active]) =>
        axios.post(`${API_URL}/api/update-listing-state`, {
          username,
          listingId,
          active
        })
      );
      
      const responses = await Promise.all(updatePromises);
      
      // Check if all updates were successful
      const allSuccessful = responses.every(response => response.data.success);
      
      if (allSuccessful) {
        // Clear changes and show success message
        setChanges({});
        setHasChanges(false);
        setShowSuccessMessage(true);
        console.log(`Successfully saved ${Object.keys(changes).length} listing state changes`);
      } else {
        throw new Error('Some listing updates failed');
      }
      
    } catch (error) {
      console.error('Error saving listing changes:', error);
      setShowErrorMessage(true);
      
      // Revert any failed changes by refetching data
      try {
        const username = 'autocentrumbb'; // Use same logic as above
        const response = await axios.get(`${API_URL}/api/autobazar-listings`, {
          params: { username }
        });
        
        if (response.data && response.data.listings) {
          setListings(response.data.listings);
          setChanges({});
          setHasChanges(false);
        }
      } catch (fetchError) {
        console.error('Error reverting changes:', fetchError);
      }
    } finally {
      setSaving(false);
    }
  };

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{t(error)}</Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 }, // Responsive padding
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {listings.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography>{t('No listings found in your Autobazar feed.')}</Typography>
        </Box>
      ) : (
        <ListingsContainer>
          {listings.map((listing) => {
            const cardElement = (
              <ListingCard
                sx={{
                  opacity: listing.active ? 1 : 0.6,
                  border: changes[listing.id || ''] !== undefined ? '2px solid #1976d2' : 'none',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onClick={() => handleListingClick(listing)}
              >
                <ListingImage
                  src={listing.photo || "https://via.placeholder.com/150?text=No+Image"}
                  alt={listing.title}
                  onError={(e) => {
                    console.log(`Image failed to load: ${listing.photo}`);
                    (e.target as HTMLImageElement).src = "https://via.placeholder.com/150?text=Image+Error";
                  }}
                />
                <ListingDetails>
                  <ListingTitle variant="subtitle1">
                    {listing.title}
                  </ListingTitle>

                  {/* Only advertised cars get re-posted, so only they have a
                      meaningful countdown - a switched-off car's timestamp is
                      just when it first appeared in the feed */}
                  {showRefreshCountdown && listing.active && formatTimeUntilRefresh(listing.uploadTimestamp, updateInterval) && (
                    <Typography
                      sx={{
                        fontSize: { xs: '12px', sm: '13px' },
                        color: '#666',
                        marginBottom: 0.5,
                        pointerEvents: 'none'
                      }}
                    >
                      {t('Obnova o')}: {formatTimeUntilRefresh(listing.uploadTimestamp, updateInterval)}
                    </Typography>
                  )}

                  {showPerListingInterval && (
                    <Box
                      onClick={(event) => event.stopPropagation()}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        marginTop: 'auto',
                        paddingTop: { xs: 0.5, sm: 1 }
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: '11px', sm: '12px' }, color: '#888' }}>
                        {t('Obnova')}
                      </Typography>
                      <IntervalSelect
                        value={listing.refreshInterval ?? ''}
                        data-override={listing.refreshInterval !== undefined ? 'true' : 'false'}
                        onChange={(event) => handleListingIntervalChange(listing.id || '', event.target.value)}
                      >
                        <option value="">{t('Podľa účtu')}</option>
                        {PER_LISTING_INTERVAL_OPTIONS.map(days => (
                          <option key={days} value={days}>{days} {t('dní')}</option>
                        ))}
                      </IntervalSelect>
                    </Box>
                  )}

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: showPerListingInterval ? 0 : 'auto',
                    paddingTop: { xs: 0.5, sm: 1 }, // Responsive padding
                    flexWrap: { xs: 'wrap', sm: 'nowrap' }, // Allow wrapping on mobile if needed
                    gap: { xs: 1, sm: 0 }
                  }}>
                    {showTopovat && (
                      <IconButton
                        size="small"
                        title={listing.topovat ? t('Topovanie zapnuté') : t('Topovať')}
                        aria-label={t('Topovať')}
                        aria-pressed={!!listing.topovat}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleToggleTopovat(listing.id || '', !listing.topovat);
                        }}
                        sx={{
                          padding: '2px',
                          marginRight: 0.5,
                          color: listing.topovat ? '#f57c00' : '#bbb',
                          '&:hover': { color: listing.topovat ? '#ef6c00' : '#888' }
                        }}
                      >
                        <ArrowCircleUpIcon sx={{ fontSize: { xs: 24, sm: 26 } }} />
                      </IconButton>
                    )}
                    {listing.price && (
                      <Typography
                        sx={{
                          color: '#e74c3c',
                          fontWeight: 'bold',
                          fontSize: { xs: '16px', sm: '18px' }, // Responsive font size
                          pointerEvents: 'none'
                        }}
                      >
                        {listing.price}
                      </Typography>
                    )}
                    <StyledSwitch
                      size="small"
                      checked={listing.active}
                      onChange={(event) => handleToggleActive(listing.id || '', event)}
                      onClick={(event) => {
                        console.log('Switch clicked - preventing propagation');
                        event.stopPropagation();
                      }}
                    />
                  </Box>
                </ListingDetails>
              </ListingCard>
            );

            return (
              <React.Fragment key={listing.id}>
                {cardElement}
              </React.Fragment>
            );
          })}
        </ListingsContainer>
      )}
      
      {/* Image Selection Popup */}
      {selectedListing && userData && (
        <ImageSelectionPopup
          isOpen={popupOpen}
          onClose={handleClosePopup}
          onSaveSuccess={handleSaveSuccess}
          listingId={selectedListing.id || ''}
          listingTitle={selectedListing.title}
          xmlLink={userData.xmlLink}
          username={userData.username}
        />
      )}

      {/* Floating Save Button */}
      {hasChanges && (
        <FloatingSaveButton
          variant="extended"
          onClick={handleSaveChanges}
          disabled={saving}
          size="large"
        >
          <SaveIcon sx={{ marginRight: 1 }} />
          {t('Uložiť')}
        </FloatingSaveButton>
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessMessage}
        autoHideDuration={3000}
        onClose={() => setShowSuccessMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setShowSuccessMessage(false)} severity="success">
          {t('Listing states saved successfully!')}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorMessage}
        autoHideDuration={4000}
        onClose={() => setShowErrorMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={() => setShowErrorMessage(false)} severity="error">
          {t('Failed to save listing states. Please try again.')}
        </Alert>
      </Snackbar>

      {/* Limit Alert Snackbar */}
      <Snackbar
        open={showLimitAlert}
        autoHideDuration={5000}
        onClose={() => setShowLimitAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowLimitAlert(false)} severity="warning" sx={{ width: '100%' }}>
          {t('Dosiahli ste limit {max} aktívnych inzerátov na účet. Deaktivujte niektoré inzeráty, ak chcete aktivovať ďalšie.', { max: MAX_ACTIVE_LISTINGS })}
        </Alert>
      </Snackbar>
    </Box>
  );
} 